import { ApiError } from '@daater/api';
import { getApiUrl } from '@daater/shared';
import { getAuthToken } from '../lib/auth-store';
import { apiClient } from '../lib/api-client';
import type {
  AnalyticHistoric,
  AnalyticsRequest,
  AnalyticsResponse,
  ApiResponse,
  AccountResponse,
  LoginPayload,
  LoginResponseData,
  MeResponse,
  PaginatedResponse,
  PayuConfirmationPayload,
  PaymentHistoryResponse,
  PaymentOrder,
  RecordsFilter,
  RegisterPayload,
  SavedReportColumn,
  SavedReportItem,
  RecordsResponse,
  SearchRecordsBody,
  TableField,
  TableFilter,
  UpdateTableSettingsPayload,
} from './types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const CURRENT_ACCOUNT_PATH = '/users/me';
const PAYMENT_ROUTE_PATH = '/payment';
const LOGIN_ROUTE_PATH = '/login';
const CHANGE_PASSWORD_ROUTE_PATH = '/change-password';

const toPositiveNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return undefined;
};

const shouldBlockForPayment = (account?: AccountResponse | null) => {
  if (!account) return false;
  if (account.statusCode === 402) return true;
  return account.status === 'trial' && typeof account.daysLeft === 'number' && account.daysLeft < 0;
};

const redirectToPayment = (amount?: unknown) => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === PAYMENT_ROUTE_PATH) return;

  const params = new URLSearchParams(window.location.search);
  const normalizedAmount = toPositiveNumber(amount);

  if (normalizedAmount !== undefined) {
    params.set('amount', String(normalizedAmount));
  }

  const nextUrl = params.toString()
    ? `${PAYMENT_ROUTE_PATH}?${params.toString()}`
    : PAYMENT_ROUTE_PATH;

  window.history.replaceState({}, '', nextUrl);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const redirectToLogin = () => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === LOGIN_ROUTE_PATH) return;
  window.history.replaceState({}, '', LOGIN_ROUTE_PATH);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const redirectToChangePassword = () => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === CHANGE_PASSWORD_ROUTE_PATH) return;
  window.history.replaceState({}, '', CHANGE_PASSWORD_ROUTE_PATH);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

/**
 * El usuario entró con la contraseña del sistema anterior: el API responde 403
 * con este código a todo lo demás. Se lleva al cambio, que es lo único que
 * puede hacer, sin importar en qué ruta haya recargado.
 */
export const isPasswordChangeRequired = (error: unknown): boolean =>
  error instanceof ApiError &&
  error.status === 403 &&
  typeof error.data === 'object' &&
  error.data !== null &&
  (error.data as { error?: string }).error === 'PASSWORD_CHANGE_REQUIRED';

const buildPaymentRequiredError = (account?: AccountResponse) =>
  new ApiError('Payment Required', 402, {
    success: true,
    statusCode: 402,
    data: account,
  });

export const normalizeCurrentAccountResponse = (data: AccountResponse): AccountResponse => {
  const user = data.user;
  const team = data.team;
  const userRecord = isRecord(user) ? user : undefined;
  const teamRecord = isRecord(team) ? team : undefined;

  return {
    ...data,
    user: typeof user === 'string' ? user : (userRecord?._id ?? user),
    team: typeof team === 'string' ? team : (teamRecord?._id ?? team),
    userData: userRecord ?? data.userData,
    teamData: teamRecord ?? data.teamData,
  };
};

const mapMeToAccountResponse = (me: MeResponse): AccountResponse => ({
  _id: me.id,
  user: me.id,
  status: me.status,
  currency: me.currency,
  daysLeft: me.daysLeft ?? undefined,
  plan: me.plan ?? undefined,
  maxReport: me.maxRegistersPerReport ?? undefined,
  table: me.lastTableId ?? undefined,
  dataset: undefined,
  settings: me.settings,
  // El acceso a detalle lo calcula el API (sectores y partidas del usuario, y
  // acceso total para admin/god-admin). Este mapeo lo descartaba, así que la
  // pantalla de detalle se quedaba sin con qué decidir y trataba a todo el
  // mundo como demo.
  detail: me.detail,
  userData: {
    _id: me.id,
    email: me.email,
    name: me.name,
    firstName: me.name,
    lastName: me.lastName ?? undefined,
  },
});

const fetchGuardedCurrentAccount = async () => {
  const me = await apiClient.get<MeResponse>(CURRENT_ACCOUNT_PATH);
  return mapMeToAccountResponse(me);
};

const ensureActiveAccountAccess = async () => {
  try {
    const account = await fetchGuardedCurrentAccount();

    if (shouldBlockForPayment(account)) {
      redirectToPayment(account.amount);
      throw buildPaymentRequiredError(account);
    }

    return account;
  } catch (error) {
    if (isPasswordChangeRequired(error)) {
      redirectToChangePassword();
      throw error;
    }

    if (error instanceof ApiError) {
      if (error.status === 401) {
        redirectToLogin();
      }

      if (error.status === 402) {
        const responseData = error.data as ApiResponse<AccountResponse> | undefined;
        const account = responseData?.data
          ? normalizeCurrentAccountResponse(responseData.data)
          : undefined;

        redirectToPayment(account?.amount);
        throw buildPaymentRequiredError(account);
      }
    }

    throw error;
  }
};

const guardedGet = async <T>(path: string) => {
  await ensureActiveAccountAccess();
  return apiClient.get<T>(path);
};

const guardedPost = async <T>(path: string, body?: unknown) => {
  await ensureActiveAccountAccess();
  return apiClient.post<T>(path, body);
};

export const loginRequest = async (payload: LoginPayload) => {
  return apiClient.post<LoginResponseData>('/auth/login', payload);
};

/**
 * Avisa al API que la sesión terminó para que deje de ocupar un cupo del
 * máximo de sesiones. Tiene que ir antes de borrar el token local, que es con
 * el que se identifica la sesión. Si falla (sin red, token ya vencido) no
 * importa: la salida sigue igual y la sesión se descuenta sola al vencer.
 */
export const logoutRequest = async (): Promise<void> => {
  try {
    await apiClient.post('/auth/logout');
  } catch {
    // Nunca debe impedir que el usuario salga.
  }
};

export const updateTerms = async () => {
  return apiClient.put<ApiResponse<Record<string, unknown>>>('/accounts/update/', {
    terms: true,
  });
};

export const fetchCurrentAccount = async () => {
  return fetchGuardedCurrentAccount();
};

export const fetchCurrentTeam = async (userId: string) => {
  const response =
    await guardedGet<
      ApiResponse<{ items: Array<{ user: string; team: Record<string, unknown> }> }>
    >('/accounts/team');
  const items = response.data?.items ?? [];
  const match = items.find(item => item.user === userId);
  return match?.team ?? null;
};

export const searchTableRecords = async (
  tableId: string,
  body: SearchRecordsBody
): Promise<RecordsResponse> => {
  await ensureActiveAccountAccess();
  return apiClient.post<RecordsResponse>(`/tables/${tableId}/records/search`, body);
};

export const fetchTableFields = async (tableId: string) => {
  const response = await guardedGet<PaginatedResponse<TableField>>(
    `/tables/${tableId}/fields?limit=200&sortBy=order&isPublic=true`
  );
  return response.data ?? [];
};

export const fetchTableFilters = async (tableId: string) => {
  const response = await guardedGet<PaginatedResponse<TableFilter>>(
    `/tables/${tableId}/filters?limit=200&sortBy=order`
  );
  return response.data ?? [];
};

export const fetchFilterAutocomplete = async (
  tableId: string,
  filterId: string,
  query: string,
  limit = 20
) => {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  params.set('limit', String(limit));
  const response = await guardedGet<{ data: Array<{ value: string; name: string }> }>(
    `/tables/${tableId}/filters/${filterId}/autocomplete?${params.toString()}`
  );
  return response.data ?? [];
};

export const requestPasswordRecovery = async (email: string) => {
  return apiClient.post<ApiResponse<Record<string, unknown>>>('/auth/recovery', { email });
};

export const resetPassword = async (payload: {
  token: string;
  password: string;
  repassword: string;
}) => {
  return apiClient.post<ApiResponse<Record<string, unknown>>>('/auth/reset', {
    token: payload.token,
    password: payload.password,
  });
};

export const changePassword = async (payload: {
  currentPassword?: string;
  newPassword: string;
}) => {
  return apiClient.post<{ token: string }>('/auth/change-password', payload);
};

export const switchAccount = (_payload: {
  account: string;
  dataset: string;
  table?: string;
}): Promise<ApiResponse<Record<string, unknown>>> => {
  // Legacy POST /accounts/switch isn't on the new API yet. Resolve locally so
  // the mutation's onSuccess can still update the account store; the records
  // query is keyed by the URL's tableId and doesn't need server-side state.
  // Tracked in docs/superpowers/data-migration.md — restore the network call
  // when the account/session endpoints get migrated.
  return Promise.resolve({ data: {}, success: true });
};

export const requestLegacyExport = async (payload: Record<string, unknown>) => {
  const response = await guardedPost<ApiResponse<SavedReportItem>>('/accounts/reports', payload);
  return response.data;
};

export const fetchReportStatus = async () => {
  const response = await guardedPost<ApiResponse<{ status?: string }>>('/accounts/reports/status');
  return response.data;
};

export const createSavedReport = async (
  tableId: string,
  payload: {
    name: string;
    filters: RecordsFilter[];
    columns: SavedReportColumn[];
    analysis?: unknown[];
    detailed?: boolean;
    demoMode?: boolean;
  }
): Promise<SavedReportItem> => {
  return apiClient.post<SavedReportItem>(`/tables/${tableId}/reports`, payload);
};

export const fetchSavedReports = async (
  tableId: string
): Promise<PaginatedResponse<SavedReportItem>> => {
  return apiClient.get<PaginatedResponse<SavedReportItem>>(
    `/tables/${tableId}/reports?limit=200&sortBy=updatedAt&sortDir=desc`
  );
};

export const deleteSavedReport = async (tableId: string, reportId: string): Promise<void> => {
  await apiClient.del(`/tables/${tableId}/reports/${reportId}`);
};

export const updateUserTableSettings = async (payload: UpdateTableSettingsPayload) => {
  return apiClient.patch<Record<string, unknown>>(`/users/me/settings/tables/${payload.tableId}`, {
    columns: payload.columns,
  });
};

export const createPaymentOrder = async (): Promise<PaymentOrder> =>
  apiClient.post<PaymentOrder>('/payments/order');

export const confirmPaymentFromPayu = async (payload: PayuConfirmationPayload) =>
  apiClient.post<Record<string, unknown>>('/payments/confirmation', payload);

export const fetchPaymentHistory = async (page = 1) => {
  const path = page > 1 ? `/accounts/payments?page=${page}` : '/accounts/payments';
  const response = await guardedGet<ApiResponse<PaymentHistoryResponse>>(path);
  return response.data;
};

export const registerUserRequest = async (payload: RegisterPayload) => {
  return apiClient.post<Record<string, unknown>>('/users/register', payload);
};

export const updateLastTable = async (tableId: string): Promise<void> => {
  await apiClient.patch('/users/me/last-table', { tableId });
};

export const trackExportEvent = async (payload: {
  tableId: string;
  tableName: string;
  rowCount: number;
  filters: RecordsFilter[];
}): Promise<void> => {
  await apiClient.post('/analytics/events', payload);
};

export type DataStructureDataset = {
  _id: string;
  name: string;
  section: string;
  countryCode: string | null;
};

export type DataStructureTable = {
  _id: string;
  name: string;
  datasetId: string;
  datasetName: string;
  countryCode: string | null;
  isDetailTable: boolean;
};

export type DataStructureResponse = {
  datasets: DataStructureDataset[];
  tables: DataStructureTable[];
  informes: Record<string, unknown>[];
};

export const fetchDataStructure = async (): Promise<DataStructureResponse> => {
  return apiClient.get<DataStructureResponse>('/accounts/data_structure');
};

export const fetchAnalyticsHistoric = async (): Promise<AnalyticHistoric[]> => {
  return apiClient.get<AnalyticHistoric[]>('/accounts/analytics/historic');
};

export const fetchTableAnalytics = async (
  tableId: string,
  body: AnalyticsRequest
): Promise<AnalyticsResponse> => {
  await ensureActiveAccountAccess();
  return apiClient.post<AnalyticsResponse>(`/tables/${tableId}/records/analytics`, body);
};

export const exportTableRecords = async (
  tableId: string,
  filters: RecordsFilter[],
  columns?: string[]
): Promise<void> => {
  const token = getAuthToken();
  const baseUrl = getApiUrl();

  const response = await fetch(`${baseUrl}/tables/${tableId}/records/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ filters, ...(columns?.length ? { columns } : {}) }),
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => ({}))) as { message?: string };
    throw new ApiError(data?.message ?? 'Error al exportar', response.status, data);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? `reporte-${Date.now()}.xlsx`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
